import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { X, Copy, Check, Loader2, Link as LinkIcon } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PaymentLinkModal({ quote, token, onClose, onCreated }) {
  const [amount, setAmount] = useState("");
  const [description, setDescription] = useState(
    quote ? `Devis ${quote.name} — ${quote.service_type}` : ""
  );
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [copied, setCopied] = useState(false);

  const headers = { Authorization: `Bearer ${token}` };
  const origin = window.location.origin;

  const submit = async (e) => {
    e.preventDefault();
    const a = parseFloat(amount);
    if (!a || a <= 0) {
      toast.error("Montant invalide");
      return;
    }
    if (!description.trim()) {
      toast.error("Description requise");
      return;
    }
    setLoading(true);
    try {
      const { data } = await axios.post(
        `${API}/admin/payment-links`,
        {
          amount: a,
          description: description.trim(),
          client_name: quote?.name || null,
          client_email: quote?.email || null,
          quote_id: quote?.id || null,
        },
        { headers }
      );
      setResult(data);
      toast.success("Lien de paiement créé");
      onCreated?.(data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Échec création");
    } finally {
      setLoading(false);
    }
  };

  const link = result ? `${origin}/paiement/${result.slug}` : "";

  const copy = async () => {
    await navigator.clipboard.writeText(link);
    setCopied(true);
    toast.success("Lien copié");
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div
      data-testid="payment-link-modal"
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        className="relative w-full max-w-lg gradient-border rounded-2xl p-6 sm:p-8"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={onClose}
          data-testid="modal-close"
          className="absolute top-4 right-4 flex h-9 w-9 items-center justify-center rounded-md border border-white/10 bg-white/5 hover:bg-white/10"
        >
          <X className="h-4 w-4" />
        </button>

        <div className="flex items-center gap-2">
          <LinkIcon className="h-5 w-5 text-violet-300" />
          <h2 className="font-display text-xl font-bold">
            Générer un lien de paiement
          </h2>
        </div>
        {quote && (
          <p className="mt-1 text-sm text-zinc-400">
            Pour <span className="text-white">{quote.name}</span> ·{" "}
            {quote.email}
          </p>
        )}

        {!result ? (
          <form onSubmit={submit} className="mt-6 space-y-4">
            <div>
              <Label className="text-zinc-300 text-xs">Montant (€)*</Label>
              <Input
                type="number"
                step="0.01"
                min="1"
                data-testid="pl-amount"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                placeholder="1500.00"
                className="mt-2 bg-zinc-900/60 border-white/10 focus-visible:ring-violet-500 text-white"
              />
            </div>
            <div>
              <Label className="text-zinc-300 text-xs">Description*</Label>
              <Textarea
                data-testid="pl-description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                rows={3}
                placeholder="Acompte 50% PC clé en main #023"
                className="mt-2 bg-zinc-900/60 border-white/10 focus-visible:ring-violet-500 text-white"
              />
              <p className="mt-1.5 text-[11px] text-zinc-500">
                Le client verra ce texte sur la page de paiement.
              </p>
            </div>

            <button
              type="submit"
              disabled={loading}
              data-testid="pl-submit"
              className="mt-2 inline-flex w-full items-center justify-center gap-2 rounded-md bg-violet-600 hover:bg-violet-500 disabled:opacity-60 px-5 py-3 text-sm font-semibold text-white shadow-violet"
            >
              {loading ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                "Créer le lien de paiement"
              )}
            </button>
          </form>
        ) : (
          <div className="mt-6">
            <p className="text-xs uppercase font-mono tracking-[0.18em] text-violet-400">
              Lien généré
            </p>
            <div
              data-testid="pl-result"
              className="mt-3 flex items-center gap-2 rounded-md border border-violet-500/40 bg-violet-500/10 p-3"
            >
              <code className="flex-1 text-xs sm:text-sm text-violet-200 break-all">
                {link}
              </code>
              <button
                onClick={copy}
                data-testid="pl-copy"
                className="flex h-9 w-9 flex-none items-center justify-center rounded-md bg-violet-600 hover:bg-violet-500"
              >
                {copied ? (
                  <Check className="h-4 w-4" />
                ) : (
                  <Copy className="h-4 w-4" />
                )}
              </button>
            </div>
            <div className="mt-4 grid grid-cols-2 gap-3 text-sm">
              <div className="rounded-md border border-white/10 bg-zinc-900/50 p-3">
                <p className="text-[10px] uppercase tracking-wider text-zinc-500">
                  Montant
                </p>
                <p className="mt-1 font-display text-lg font-bold">
                  {result.amount.toFixed(2)}€
                </p>
              </div>
              <div className="rounded-md border border-white/10 bg-zinc-900/50 p-3">
                <p className="text-[10px] uppercase tracking-wider text-zinc-500">
                  Statut
                </p>
                <p className="mt-1 font-display text-lg font-bold text-violet-300">
                  En attente
                </p>
              </div>
            </div>
            <p className="mt-5 text-xs text-zinc-400 leading-relaxed">
              Copie ce lien et envoie-le à ton client par email ou WhatsApp. Tu
              recevras un email dès qu'il aura payé.
            </p>
            <button
              onClick={onClose}
              className="mt-5 w-full rounded-md border border-white/10 bg-white/5 hover:bg-white/10 px-5 py-2.5 text-sm font-semibold"
            >
              Fermer
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
